import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA,ChangeDetectionStrategy, Component, ElementRef, OnDestroy, ViewEncapsulation, inject} from '@angular/core';
import { Router } from '@angular/router';

type SearchProvider = { name: string; site: string };
const BASE_ORIGIN = 'https://www.louplogistics.com';

const HEADER_MENU = [
  { name: 'Careers', site: '/careers/index.html'},
  { name: 'Rates', site: 'resource-center/rates.html' },
  { name: 'Facilities', site: '/slt/apps/external-web/#/search-transload-facility'}
];

const SEARCH_WHAT_WE_DO = [
  { name: 'Door-to-Door Shipping', site: '/what-we-do/door-to-door-shipping.html'},
  { name: 'Logistics', site: '/what-we-do/logistics.html' },
  { name: 'Specialized Services', site: '/what-we-do/specialized-services.html'},
];

const SEARCH_SOLUTIONS = [
  { name: 'Cost', site: '/solutions/cost.html' },
  { name: 'Time', site: '/solutions/time.html' },
  { name: 'Visibility', site: '/solutions/visibility.html' },
  { name: 'Access & Capacity', site: '/solutions/access-capacity.html' },
  { name: 'Supply Chain Management', site: '/solutions/supply-chain-management.html' },
  { name: 'Consulting Services', site: '/solutions/consulting.html' },
];

const SEARCH_RESOURCE_CENTER = [
  { name: 'Rail 101', site: '/resource-center/rail-101.html' },
  { name: 'What Is Transloading?', site: '/resource-center/what-is-transloading.html'},
  { name: 'What Is a Transloading Facility?', site: '/resource-center/what-is-a-railport.html'},
  { name: 'Find a Transload Facility', site: '/slt/apps/external-web/#/search-transload-facility' },
  { name: 'Rates', site: '/resource-center/rates.html' },
  { name: 'Proper Loading Procedures', site: '/resource-center/proper-loading-procedures.html'},
  { name: 'Glossary', site: '/resource-center/glossary.html' },
  { name: 'Certifications & Forms', site: '/resource-center/forms.html' },
  { name: 'Loup Transload & Supply Chain	Services Menu', site: '/resource-center/services_menu.html'},
];

@Component({
  selector: 'lib-external-header',
  templateUrl: './external-header.component.html',
  styleUrls: ['./external-header.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class ExternalHeaderComponent implements AfterViewInit, OnDestroy {
  selectedText = '';
  headerMenu = HEADER_MENU
  whatWeDo = SEARCH_WHAT_WE_DO;
  solutions = SEARCH_SOLUTIONS;
  resourceCenter = SEARCH_RESOURCE_CENTER;
  searchQuery = '';
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly router = inject(Router);

  /** visual height of the Top Bar (in px) */
  private readonly topBarHeight = 94;

  /** current measured secondary height (in px) */
  private secondaryHeight = 88; // fallback

  /** scroll target: .content if present, else window */
  private scrollTarget: HTMLElement | Window = window;

  private readonly onScrollBound = () => this.applyOffset(this.getScrollY());
  private readonly onResizeBound = () => {
    this.measureSecondaryHeight();
    this.applyOffset(this.getScrollY());
  };

  ngAfterViewInit(): void {
    const contentEl = document.querySelector('.content') as HTMLElement | null;
    this.scrollTarget = contentEl ?? window;

    /**  Initial measure + state */
    this.measureSecondaryHeight();
    this.applyOffset(this.getScrollY());

    /** Re-measure after images/fonts */ 
    setTimeout(() => {
      this.measureSecondaryHeight();
      this.applyOffset(this.getScrollY());
    }, 0);

    (this.scrollTarget as any).addEventListener('scroll', this.onScrollBound, {
      passive: true,
    });
    window.addEventListener('resize', this.onResizeBound, { passive: true });
  }

  ngOnDestroy(): void {
    (this.scrollTarget as any)?.removeEventListener?.(
      'scroll',
      this.onScrollBound
    );
    window.removeEventListener('resize', this.onResizeBound);
  }

  private getScrollY(): number {
    if (this.scrollTarget === window) {
      return window.scrollY || document.documentElement.scrollTop || 0;
    }
    return (this.scrollTarget as HTMLElement).scrollTop;
  }

  /** Measure Secondary Bar height and expose to CSS */
  private measureSecondaryHeight(): void {
    const el = this.host.nativeElement.querySelector(
      '.secondary-bar'
    ) as HTMLElement | null;
    if (!el) return;
    const h = Math.ceil(el.getBoundingClientRect().height);
    this.secondaryHeight = h;
    document.documentElement.style.setProperty(
      '--secondary-bar-height',
      `${h}px`
    );
  }

  /** Set Secondary Bar offset + the correct content padding */
  private applyOffset(scrollY: number): void {
    const secondaryOffset =
      scrollY >= this.topBarHeight ? 0 : this.topBarHeight;
    document.documentElement.style.setProperty(
      '--secondary-offset',
      `${secondaryOffset}px`
    );

    /** Content must sit below the visible headers:
    at load: 94 + secondaryHeight; after scroll: 0 + secondaryHeight. */
    const contentPaddingTop = secondaryOffset + this.secondaryHeight;
    document.documentElement.style.setProperty(
      '--content-padding-top',
      `${contentPaddingTop}px`
    );
  }

  /** --- your existing methods --- */ 
  navigate(path: string): void {
    const target = new URL(path, BASE_ORIGIN).toString();
    this.router.navigateByUrl(target);
  }

  openExternal(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  onSearch(): void {
    const q = this.searchQuery.trim();
    if (!q) return;
    this.router.navigate(['/search'], { queryParams: { q } });
  }

  onItemDetail(event: Event) {
    const { detail }: CustomEvent = event as CustomEvent;
    const { site }: SearchProvider = detail as SearchProvider;
    /** If `site` is already absolute, use it; otherwise resolve it against the Loup origin. */ 
    const target = new URL(site, BASE_ORIGIN).toString();
    /** Open in a new tab safely (no access back to your window) */ 
    window.open(target, '_blank', 'noopener,noreferrer');
  }

   opendPage(item: any) {
    const target = new URL(item.site, BASE_ORIGIN).toString();
    window.open(target, '_blank', 'noopener,noreferrer');
  }
}
